import { Component } from '@angular/core';
import { MemberService } from '../member-service/MemberService ';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import heic2any from 'heic2any';

@Component({
  selector: 'app-dietary-preference',
  standalone: true,
  imports: [MatTableModule, CommonModule, MatPaginatorModule, MatPaginator, MatProgressSpinnerModule],
  templateUrl: './dietary-preference.component.html',
  styleUrl: './dietary-preference.component.css'
})
export class DietaryPreferenceComponent {
  documents: any[] = [];
  firstPage: number = 0;
  pageSize: number = 5;
  pageIndex: number = 0;
  totalRecords: number = 0;
  isLoading: boolean = false;
  memberId: any;
  dataSource = new MatTableDataSource<any>;
  displayedColumns: string[] = ['CreatedDateTime', 'Type', 'Actions'];
  /** True when showing <img>; false when showing iframe (e.g. PDF). */
  isImage = false;
  isLoadingPreview = false;
  previewError: string | null = null;
  /** When in-modal preview fails (e.g. HEIC + CORS), user can open the blob URL in a new tab — that often works even when fetch() is blocked. */
  previewFallbackUrl: string | null = null;
  modalPdfUrl: SafeResourceUrl | null = null;
  /** Use SafeUrl for <img [src]> — ResourceUrl can fail to render images. */
  modalImageUrl: SafeUrl | null = null;
  private previewObjectUrl: string | null = null;

  constructor(private memberService: MemberService, private route: ActivatedRoute, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.memberId = this.route.snapshot.queryParamMap.get('id') || '';
    this.getLabWork(1);
  }
  getLabWork(page: any) {
    this.isLoading = true;
    this.memberService.GetLabWork(this.memberId, page).subscribe({
      next: data => {
        const documentsdata = data;
        this.dataSource = data['result'];
        this.totalRecords = data['totalCount'];
        this.pageIndex = page - 1;
        this.isLoading = false;
      },
      error: error => {
        console.error('Error fetching lab reports:', error);
        this.isLoading = false;
      }
    });
  }

  private cleanupPreviewObjectUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }

  closePreview(): void {
    this.cleanupPreviewObjectUrl();
    this.isLoadingPreview = false;
    this.previewError = null;
    this.previewFallbackUrl = null;
    this.modalPdfUrl = null;
    this.modalImageUrl = null;
    this.isImage = false;
  }

  async openModal(document: any): Promise<void> {
    const url = String(document ?? '');
    const lower = url.toLowerCase().split('?')[0].split('#')[0];

    this.cleanupPreviewObjectUrl();
    this.previewError = null;
    this.previewFallbackUrl = null;
    this.modalPdfUrl = null;
    this.modalImageUrl = null;
    this.isLoadingPreview = true;

    const isPdf = lower.endsWith('.pdf');
    const isHeic = lower.endsWith('.heic') || lower.endsWith('.heif');
    const isRasterImage =
      lower.endsWith('.png') ||
      lower.endsWith('.jpg') ||
      lower.endsWith('.jpeg') ||
      lower.endsWith('.gif') ||
      lower.endsWith('.webp') ||
      lower.endsWith('.bmp') ||
      lower.endsWith('.svg');

    try {
      if (isPdf) {
        this.isImage = false;
        this.modalPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        return;
      }

      if (isHeic) {
        this.isImage = true;
        this.previewFallbackUrl = url;
        try {
          const res = await fetch(url, { cache: 'no-store', mode: 'cors' });
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          const blob = await res.blob();
          const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.9 });
          const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
          this.previewObjectUrl = URL.createObjectURL(jpegBlob as Blob);
          this.modalImageUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewObjectUrl);
          this.previewFallbackUrl = null;
        } catch {
          // HEIC cannot be shown in <img> in Chrome without converting. Conversion needs to read bytes via fetch(),
          // which requires the blob storage account to allow this site's origin in CORS (GET).
          this.previewError =
            'HEIC files cannot be shown inline in this browser until the file is downloaded. That download is blocked unless Azure Blob Storage CORS allows GET from this app’s origin (e.g. localhost:4200 and your production URL). Ask your admin to add those origins, or convert images to JPEG/PNG on upload.';
        }
        return;
      }

      if (isRasterImage) {
        this.isImage = true;
        this.modalImageUrl = this.sanitizer.bypassSecurityTrustUrl(url);
        return;
      }

      this.previewError = 'Unsupported file type for preview.';
      this.previewFallbackUrl = url;
      this.isImage = false;
    } finally {
      this.isLoadingPreview = false;
    }
  }

  pageChangeEvent(page: any) {
    this.getLabWork(page.pageIndex + 1);
  }
}
